import React, { useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useQueryClient } from "@tanstack/react-query";
import { useCommentsAndLocations } from "../../../hooks/commentsAndResidenceQuery";
import {
  useNotification,
  NotificationSeverity,
} from "../../../context/NotificationContext";

import LoadingAnimation from "../../loading/LoadingAnimation";
import CommentsLocationsDrawer from "../drawner/CommentsLocationsDrawer";
import CustomDatePicker from "../CustomDatePicker";
import CustomInputField from "../inputs/CustomInputField";
import { Tooltip } from "@mui/material";

// Iconos
import UploadSuccessIcon from "../../../assets/svg/view/upload_success.svg";
import SaveIcon from "../../../assets/svg/components/save.svg";
import LoadingIcon from "../../../assets/svg/view/loading.svg";
import LoadingLightIcon from "../../../assets/svg/view/loading_light.svg";
import CalendarIcon from "../../../assets/svg/components/calendar.svg";
import CalendarLightIcon from "../../../assets/svg/components/calendar_light.svg";
import TrashIcon from "../../../assets/svg/components/trash.svg";
import SeeIcon from "../../../assets/svg/components/see.svg";
import UpdatedIcon from "../../../assets/svg/components/updated.svg";
import ComentIcon from "../../../assets/svg/components/coment.svg";
import AddIcon from "../../../assets/svg/view/arrow_up.svg";
import "../../../assets/scss/components/user/UserDocumentsSectionProfile.scss";

// Constante para documentos que no expiran (usuarios)
export const NON_EXPIRABLE_USER_DOCUMENTS = [
  "Foto de Perfil",
  "Documento de identidad",
  "Certificados Laborales"
];

export default function DocumentsSectionProfile({
  documents,
  onDocumentsChange,
  dataTypes,
  isLoading,
  canEdit,
  onPreview,
  onUpdate,
  onDelete,
  onAddDocument,
  theme,
}) {
  const { showNotification } = useNotification();
  const queryClient = useQueryClient();
  const {
    useCommentsByReference,
    createComment,
    updateComment,
    deleteComment,
  } = useCommentsAndLocations();

  const [commentDrawerOpen, setCommentDrawerOpen] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);
  const dateIconSrc = theme === "dark" ? CalendarLightIcon : CalendarIcon;
  const loadingIconSrc = theme === "dark" ? LoadingLightIcon : LoadingIcon;
  
  const [modifiedDocs, setModifiedDocs] = useState({});

  const hasChanges = useCallback(
    (docId) => {
      return modifiedDocs[docId] !== undefined;
    },
    [modifiedDocs]
  );

  const { data: documentComments } = useCommentsByReference(
    selectedDocumentId || ""
  );

  // Manejadores para comentarios
  const handleOpenComments = (docId) => {
    setSelectedDocumentId(docId);
    setCommentDrawerOpen(true);
  };

  const handleCloseComments = () => {
    setCommentDrawerOpen(false);
  };

  const handleCreateComment = async (commentData) => {
    try {
      await createComment({ ...commentData, reference_id: selectedDocumentId });
      queryClient.invalidateQueries(["comments", selectedDocumentId]);
      showNotification({
        message: "Comentario creado exitosamente",
        severity: NotificationSeverity.SUCCESS,
      });
    } catch (error) {
      showNotification({
        message:
          error.response?.data?.message || "Error al crear el comentario",
        severity: NotificationSeverity.ERROR,
      });
    }
  };

  const handleUpdateComment = async (commentId, commentData) => {
    try {
      await updateComment({
        commentId,
        data: { ...commentData, reference_id: selectedDocumentId },
      });
      queryClient.invalidateQueries(["comments", selectedDocumentId]);
      showNotification({
        message: "Comentario actualizado exitosamente",
        severity: NotificationSeverity.SUCCESS,
      });
    } catch (error) {
      showNotification({
        message:
          error.response?.data?.message || "Error al actualizar el comentario",
        severity: NotificationSeverity.ERROR,
      });
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await deleteComment(commentId);
      queryClient.invalidateQueries(["comments", selectedDocumentId]);
      showNotification({
        message: "Comentario eliminado exitosamente",
        severity: NotificationSeverity.SUCCESS,
      });
    } catch (error) {
      showNotification({
        message:
          error.response?.data?.message || "Error al eliminar el comentario",
        severity: NotificationSeverity.ERROR,
      });
    }
  };

  // Manejadores para documentos
  const handleFileSelect = (typeId, docId, event) => {
  const file = event.target.files[0];
  if (file) {
    const doc = documents.find(d => d.id === docId);
    const currentDescription = doc?.original_name_description || "";
    const typeDescription = doc?.document_type?.name || "";

    const shouldUpdateDescription = !currentDescription.trim() || 
                                   currentDescription.trim() === typeDescription.trim();
    
    setModifiedDocs((prev) => ({
      ...prev,
      [docId]: {
        ...(prev[docId] || {}),
        file: file,
        hasFileChange: true,
        ...(shouldUpdateDescription ? { 
          original_name_description: typeDescription 
        } : {}),
        fileName: file.name,
        originalFileName: file.name
      },
    }));
  }
};

  const handleUpdateDocument = (typeId, docId, field, value) => {
    setModifiedDocs((prev) => ({
      ...prev,
      [docId]: {
        ...(prev[docId] || {}),
        [field]: value,
        docId: docId,
        typeId: typeId,
      },
    }));
  };

  const handleSaveChanges = (doc) => {
    const changes = modifiedDocs[doc.id] || {};
    const updatedDoc = {
      ...doc,
      ...changes,
      document_type_id: doc.document_type?.id || doc.document_type_id,
      id: doc.id,
    };

    onUpdate(updatedDoc);

    setModifiedDocs((prev) => {
      const updated = { ...prev };
      delete updated[doc.id];
      return updated;
    });
  };

  const handleAddDocument = (typeId) => {
    if (!canEdit) return;

    const documentType = dataTypes.find(type => type.id === typeId);
    
    const randomComponent = Math.random().toString(36).substring(2, 10);
    const timestamp = Date.now();
    const uniqueId = `temp-${timestamp}-${randomComponent}`;

    const tempDoc = {
      id: uniqueId,
      file_name: "",
      file: null,
      expedition_date: null,
      expiration_date: null,
      original_name_description: documentType?.name || "",
      document_type_id: typeId,
      document_type: {
        id: typeId,
        name: documentType?.name || "",
      },
      required: false,
      isNew: true,
      isLocal: true,
    };

    onAddDocument(tempDoc);
  };

  const handleLocalDelete = (doc) => {
    onDocumentsChange({
      type: "DELETE_LOCAL_DOCUMENT",
      payload: {
        typeId: doc.document_type?.id || doc.document_type_id,
        docId: doc.id,
      },
    });

    setModifiedDocs((prev) => {
      const updated = { ...prev };
      delete updated[doc.id];
      return updated;
    });
  };

  const handleSaveNewDocument = (doc) => {
  const changes = modifiedDocs[doc.id] || {};
  
  const documentTypeId = doc.document_type?.id || doc.document_type_id;
  
  if (!documentTypeId) {
    showNotification({
      message: "Error: No se pudo identificar el tipo de documento",
      severity: NotificationSeverity.ERROR,
    });
    return;
  }
  
  const documentToUpload = {
    ...doc,
    ...changes,
    file: changes.file || doc.file,
    documentTypeId: documentTypeId,
    // SOLUCIÓN: Usar nombre del tipo como descripción por defecto
    description: changes.original_name_description || 
                doc.original_name_description || 
                doc.document_type?.name || 
                "",
    // Asegurar que el nombre del archivo se preserve
    fileName: changes.file?.name || doc.file?.name,
    originalName: changes.file?.name || doc.file?.name,
    required: changes.required !== undefined ? changes.required : doc.required || false,
    expedition_date: changes.expedition_date || doc.expedition_date || null,
    expiration_date: changes.expiration_date || doc.expiration_date || null,
  };

  if (!documentToUpload.file) {
    showNotification({
      message: "Debe seleccionar un archivo para guardar el documento",
      severity: NotificationSeverity.WARNING,
    });
    return;
  }

  onDocumentsChange({
    type: "ADD_DOCUMENT",
    payload: documentToUpload,
  });

  // Limpiar modificaciones después de guardar
  setModifiedDocs((prev) => {
    const updated = { ...prev };
    delete updated[doc.id];
    return updated;
  });
};

const getDocumentWithChanges = useCallback(
  (doc) => {
    const changes = modifiedDocs[doc.id] || {};
    return {
      ...doc,
      file: changes.file || doc.file,
      file_name: changes.fileName || changes.file?.name || doc.file_name,
      original_filename: changes.originalFileName || changes.file?.name || doc.original_filename,
      expedition_date: changes.expedition_date || doc.expedition_date,
      expiration_date: changes.expiration_date || doc.expiration_date,
      original_name_description:
        changes.original_name_description || 
        doc.original_name_description || 
        doc.document_type?.name || "",
      required: changes.required !== undefined ? changes.required : doc.required || false,
    };
  },
  [modifiedDocs]
);

  const isFieldModified = useCallback(
    (docId, fieldName) => {
      return modifiedDocs[docId] && modifiedDocs[docId][fieldName] !== undefined;
    },
    [modifiedDocs]
  );

  // Función para determinar el estado del documento
  const getDocumentStatus = (doc) => {
    const isLocalDoc = doc.id?.startsWith("temp-") || doc.isLocal;
    
    if (isLocalDoc) {
      return "pending";
    }
    
    if (!doc.expiration_date || NON_EXPIRABLE_USER_DOCUMENTS.includes(doc.document_type?.name)) {
      return "valid";
    }
    
    const now = new Date();
    const expirationDate = new Date(doc.expiration_date);
    const timeDiff = expirationDate.getTime() - now.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    
    if (daysDiff < 0) {
      return "expired";
    } else if (daysDiff <= 30) {
      return "critical";
    } else if (daysDiff <= 60) {
      return "warning";
    } else {
      return "valid";
    }
  };

  // Normalizar documentos para mostrar
  const normalizeDocuments = useMemo(() => {
    return (documents = []) =>
      documents.map((doc) => ({
        ...doc,
        document_type: doc.document_type || {
          id: doc.document_type_id,
          name: doc.document_type_name || "",
          description: doc.document_type_description || "",
        },
      }));
  }, []);

  // Crear estructura de datos para tabla unificada
  const tableData = useMemo(() => {
    const normalizedDocs = normalizeDocuments(documents);
    const result = [];

    // Agregar todos los tipos de documentos
    dataTypes.forEach(type => {
      const typeDocuments = normalizedDocs.filter(
        doc => (doc.document_type?.id || doc.document_type_id) === type.id
      );

      if (typeDocuments.length === 0) {
        // Agregar fila vacía para tipos sin documentos
        result.push({
          typeId: type.id,
          typeName: type.name,
          typeDescription: type.description,
          isEmpty: true,
          documents: []
        });
      } else {
        // Agregar documentos existentes
        typeDocuments.forEach(doc => {
          result.push({
            typeId: type.id,
            typeName: type.name,
            typeDescription: type.description,
            isEmpty: false,
            document: doc,
            documents: typeDocuments
          });
        });
      }
    });

    return result;
  }, [documents, dataTypes, normalizeDocuments]);

  const renderActionButtons = useCallback(
    (doc) => {
      if (!canEdit) return null;

      const isLocalDoc = doc.id?.startsWith("temp-") || doc.isLocal;
      const hasDocChanges = hasChanges(doc.id);

      const tooltipProps = {
        arrow: true,
        placement: "top",
        enterDelay: 500,
        leaveDelay: 200,
        sx: {
          "& .MuiTooltip-tooltip": {
            backgroundColor: "rgba(33, 33, 33, 0.9)",
            color: "#ffffff",
            fontSize: "0.75rem",
            padding: "6px 12px",
            borderRadius: "4px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
          },
          "& .MuiTooltip-arrow": {
            color: "rgba(33, 33, 33, 0.9)",
          },
        },
      };

      return (
        <div className={`GM__${theme}-action-buttons-container`}>
          {!isLocalDoc &&
            (doc.file_name?.toLowerCase().endsWith(".pdf") ||
              doc.file_name?.toLowerCase().match(/\.(jpe?g|png|gif|webp)$/)) && (
              <Tooltip title="Previsualizar documento" {...tooltipProps}>
                <button
                  className={`GM__${theme}-preview-btn`}
                  onClick={() => onPreview(doc)}
                >
                  <img
                    src={SeeIcon}
                    alt="Previsualizar"
                    className={`GM__${theme}-action-icon`}
                  />
                </button>
              </Tooltip>
            )}

          <Tooltip
            title={
              isLocalDoc
                ? "Guardar documento"
                : hasDocChanges
                ? "Guardar cambios"
                : "Actualizar documento"
            }
            {...tooltipProps}
          >
            <span>
              <button
                className={`GM__${theme}-${
                  isLocalDoc 
                    ? "save" 
                    : hasDocChanges 
                      ? "modified" 
                      : "update"
                }-btn`}
                onClick={() =>
                  isLocalDoc
                    ? handleSaveNewDocument(doc)
                    : hasDocChanges
                    ? handleSaveChanges(doc)
                    : null
                }
                disabled={!isLocalDoc && !hasDocChanges}
              >
                <img
                  src={
                    isLocalDoc 
                      ? SaveIcon 
                      : hasDocChanges 
                        ? SaveIcon 
                        : UpdatedIcon
                  }
                  alt={
                    isLocalDoc
                      ? "Guardar"
                      : hasDocChanges
                      ? "Guardar cambios"
                      : "Actualizar"
                  }
                  className={`GM__${theme}-action-icon`}
                />
              </button>
            </span>
          </Tooltip>

          <Tooltip
            title={isLocalDoc ? "Eliminar entrada" : "Eliminar documento"}
            {...tooltipProps}
            sx={{
              ...tooltipProps.sx,
              "& .MuiTooltip-tooltip": {
                ...tooltipProps.sx["& .MuiTooltip-tooltip"],
                backgroundColor: "rgba(211, 47, 47, 0.9)",
              },
              "& .MuiTooltip-arrow": {
                color: "rgba(211, 47, 47, 0.9)",
              },
            }}
          >
            <button
              className={`GM__${theme}-delete-btn`}
              onClick={() =>
                isLocalDoc
                  ? handleLocalDelete(doc)
                  : onDelete({
                      id: doc.id,
                      name:
                        doc.original_name_description ||
                        doc.file_name ||
                        "Documento sin nombre",
                    })
              }
            >
              <img
                src={TrashIcon}
                alt="Eliminar"
                className={`GM__${theme}-action-icon`}
              />
            </button>
          </Tooltip>

          <Tooltip
            title="Comentario del documento"
            {...tooltipProps}
            sx={{
              ...tooltipProps.sx,
              "& .MuiTooltip-tooltip": {
                ...tooltipProps.sx["& .MuiTooltip-tooltip"],
                backgroundColor: "rgb(22, 64, 213)",
              },
              "& .MuiTooltip-arrow": {
                color: "rgb(22, 64, 213)",
              },
            }}
          >
            <button
              className={`GM__${theme}-coment-btn`}
              onClick={() => handleOpenComments(doc.id)}
            >
              <img
                src={ComentIcon}
                alt="Comentar"
                className={`GM__${theme}-action-icon`}
              />
            </button>
          </Tooltip>
        </div>
      );
    },
    [canEdit, onPreview, onDelete, hasChanges, theme]
  );

  if (isLoading) return <LoadingAnimation />;

  return (
    <div className={`GM__${theme}-user-documents-section`}>
      <div className={`GM__${theme}-documents-table-container`}>
        <table className={`GM__${theme}-documents-table`}>
          {/* SOLUCIÓN: Colgroup para anchos fijos */}
          <colgroup>
            <col style={{ width: '80px' }} />    {/* Estado */}
            <col style={{ width: '200px' }} />   {/* Tipo de Documento */}
            <col style={{ width: '220px' }} />   {/* Archivo */}
            <col style={{ width: '220px' }} />   {/* Expedición */}
            <col style={{ width: '220px' }} />   {/* Vencimiento */}
            <col style={{ width: '240px' }} />   {/* Descripción */}
            <col style={{ width: '120px' }} />   {/* Requerido */}
            <col style={{ width: '200px' }} />   {/* Acciones */}
          </colgroup>
          
          <thead>
            <tr>
              <th>Estado</th>
              <th>Tipo de Documento</th>
              <th>Archivo</th>
              <th>Expedición</th>
              <th>Vencimiento</th>
              <th>Descripción</th>
              <th>Requerido</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {tableData.map((row, index) => {
              if (row.isEmpty) {
                // Fila vacía para tipos sin documentos
                return (
                  <tr key={`empty-${row.typeId}`} className={`GM__${theme}-document-row`}>
                    <td>
                      <div className={`GM__${theme}-status-indicator pending`}></div>
                    </td>
                    <td>
                      <div className={`GM__${theme}-document-info`}>
                        <span className={`GM__${theme}-document-name`}>{row.typeName}</span>
                        {canEdit && (
                          <button
                            className={`GM__${theme}-add-document-inline`}
                            onClick={() => handleAddDocument(row.typeId)}
                            title={`Agregar ${row.typeName}`}
                          >
                            <img src={AddIcon} alt="Agregar" />
                          </button>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={`GM__${theme}-no-file`}>No hay archivo</span>
                    </td>
                    <td>
                      <span className={`GM__${theme}-placeholder-date`}>dd/mm/aaaa</span>
                    </td>
                    <td>
                      <span className={`GM__${theme}-placeholder-date`}>dd/mm/aaaa</span>
                    </td>
                    <td>
                      <span className={`GM__${theme}-no-description`}>Sin descripción</span>
                    </td>
                    <td>
                      <div className={`GM__${theme}-required-toggle`}>
                        <input type="checkbox" disabled />
                      </div>
                    </td>
                    <td>
                      {/* Celda vacía para acciones */}
                    </td>
                  </tr>
                );
              }

              // Fila con documento existente
              const doc = row.document;
              const displayDoc = getDocumentWithChanges(doc);
              const hasDocChanges = hasChanges(doc.id);
              const isLocalDoc = doc.id?.startsWith("temp-") || doc.isLocal;
              const documentStatus = getDocumentStatus(displayDoc);
              
              const nameModifiedClass = isFieldModified(doc.id, "original_name_description") ? "modified" : "";
              const expModifiedClass = isFieldModified(doc.id, "expedition_date") ? "modified" : "";
              const expirModifiedClass = isFieldModified(doc.id, "expiration_date") ? "modified" : "";
              const reqModifiedClass = isFieldModified(doc.id, "required") ? "modified" : "";
              const fileModifiedClass = isFieldModified(doc.id, "file") ? "modified" : "";
              
              return (
                <tr
                  key={doc.id}
                  className={`GM__${theme}-document-row ${
                    hasDocChanges ? `GM__${theme}-modified-row` : ""
                  }`}
                >
                  <td>
                    <div className={`GM__${theme}-status-indicator ${documentStatus}`}></div>
                  </td>
                  <td>
                    <div className={`GM__${theme}-document-info`}>
                      <span className={`GM__${theme}-document-name`}>{row.typeName}</span>
                    </div>
                  </td>
                  <td>
                    <div className={`GM__${theme}-file-cell`}>
                      {canEdit && (
                        <div className={`GM__${theme}-file-upload-inline`}>
                          <input
                            type="file"
                            onChange={(e) =>
                              handleFileSelect(
                                doc.document_type?.id || doc.document_type_id,
                                doc.id,
                                e
                              )
                            }
                            accept=".pdf,.jpg,.jpeg,.png"
                            id={`file-${doc.id}`}
                            style={{ display: "none" }}
                          />
                          <label
                            htmlFor={`file-${doc.id}`}
                            className={`GM__${theme}-file-label-inline ${fileModifiedClass}`}
                          >
                            <span className={`GM__${theme}-file-name-text`}>
                              {modifiedDocs[doc.id]?.file
                                ? modifiedDocs[doc.id].file.name
                                : doc.file
                                ? doc.file.name
                                : doc.original_filename ||
                                  doc.file_name ||
                                  "Seleccionar archivo"}
                            </span>
                          </label>
                        </div>
                      )}
                      {!canEdit && (
                        <span className={`GM__${theme}-file-name-readonly`}>
                          {doc.original_filename || doc.file_name || "Sin archivo"}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className={`GM__${theme}-date-cell`}>
                      {canEdit ? (
                        <CustomDatePicker
                          value={displayDoc.expedition_date}
                          onChange={(date) =>
                            handleUpdateDocument(
                              row.typeId,
                              doc.id,
                              "expedition_date",
                              date
                            )
                          }
                          icon={<img src={dateIconSrc} alt="Calendar" />}
                          disabled={!canEdit}
                          className={`GM__${theme}-date-picker-inline ${expModifiedClass}`}
                          theme={theme}
                        />
                      ) : (
                        <span className={`GM__${theme}-date-readonly`}>
                          {displayDoc.expedition_date 
                            ? new Date(displayDoc.expedition_date).toLocaleDateString()
                            : "dd/mm/aaaa"
                          }
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className={`GM__${theme}-date-cell`}>
                      {canEdit ? (
                        <CustomDatePicker
                          value={displayDoc.expiration_date}
                          onChange={(date) =>
                            handleUpdateDocument(
                              row.typeId,
                              doc.id,
                              "expiration_date",
                              date
                            )
                          }
                          icon={<img src={dateIconSrc} alt="Calendar" />}
                          disabled={!canEdit}
                          className={`GM__${theme}-date-picker-inline ${expirModifiedClass}`}
                          theme={theme}
                        />
                      ) : (
                        <span className={`GM__${theme}-date-readonly`}>
                          {displayDoc.expiration_date 
                            ? new Date(displayDoc.expiration_date).toLocaleDateString()
                            : "dd/mm/aaaa"
                          }
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className={`GM__${theme}-description-cell`}>
                      {canEdit ? (
                        <CustomInputField
                          type="text"
                          value={displayDoc.original_name_description || ""}
                          onChange={(e) =>
                            handleUpdateDocument(
                              row.typeId,
                              doc.id,
                              "original_name_description",
                              e.target.value
                            )
                          }
                          placeholder="Descripción del documento"
                          theme={theme}
                          className={`GM__${theme}-description-input ${nameModifiedClass}`}
                        />
                      ) : (
                        <span className={`GM__${theme}-description-readonly`}>
                          {displayDoc.original_name_description || "Sin descripción"}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className={`GM__${theme}-required-toggle`}>
                      <input
                        type="checkbox"
                        checked={displayDoc.required || false}
                        onChange={(e) =>
                          handleUpdateDocument(
                            row.typeId,
                            doc.id,
                            "required",
                            e.target.checked
                          )
                        }
                        disabled={!canEdit}
                        className={`GM__${theme}-checkbox-inline ${reqModifiedClass}`}
                      />
                    </div>
                  </td>
                  <td>
                    <div className={`GM__${theme}-actions-cell`}>
                      {renderActionButtons(doc)}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <CommentsLocationsDrawer
        open={commentDrawerOpen}
        onClose={handleCloseComments}
        type="comments"
        data={documentComments?.data || []}
        onEdit={handleUpdateComment}
        onCreate={handleCreateComment}
        onDelete={handleDeleteComment}
        theme={theme}
      />
    </div>
  );
}

DocumentsSectionProfile.propTypes = {
  documents: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      document_type_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      document_type: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        description: PropTypes.string,
      }),
      file_name: PropTypes.string,
      file_path: PropTypes.string,
      expedition_date: PropTypes.string,
      expiration_date: PropTypes.string,
      original_name_description: PropTypes.string,
      required: PropTypes.bool,
      uploaded_at: PropTypes.string,
    })
  ).isRequired,
  onDocumentsChange: PropTypes.func.isRequired,
  dataTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      description: PropTypes.string,
    })
  ),
  isLoading: PropTypes.bool,
  canEdit: PropTypes.bool,
  onPreview: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onAddDocument: PropTypes.func,
  theme: PropTypes.oneOf(["light", "dark"]).isRequired,
};